import { socket } from '../services/Socket';

// Socket.IO debugging utility
export const socketDebug = {
  // Get current connection status
  getStatus: () => ({
    connected: socket.connected,
    id: socket.id,
    transport: socket.io?.engine?.transport?.name,
    url: socket.io?.uri,
    reconnecting: socket.io?.reconnecting,
    authenticated: socket.auth || false // This might not be available, depends on your implementation
  }),

  // Test socket connection
  testConnection: () => {
    console.log('=== Socket.IO Connection Test ===');
    const status = socketDebug.getStatus();
    console.log('Status:', status);
    
    if (!status.connected) {
      console.log('❌ Socket not connected. Attempting to connect...');
      socket.connect();
    } else {
      console.log('✅ Socket connected successfully');
    }
    
    return status;
  },

  // Test message sending (for debugging)
  testMessage: (channel, data) => {
    if (!socket.connected) {
      console.error('❌ Cannot send test message: Socket not connected');
      return false;
    }
    
    console.log(`📤 Sending test message to channel: ${channel}`, data);
    socket.emit(channel, data);
    return true;
  },

  // Listen for test messages
  listenForTest: (channel, callback) => {
    console.log(`👂 Listening for messages on channel: ${channel}`);
    socket.on(channel, (data) => {
      console.log(`📥 Received message on ${channel}:`, data);
      if (callback) callback(data);
    });
  },

  // Stop listening for test messages
  stopListening: (channel) => {
    console.log(`🔇 Stopped listening on channel: ${channel}`);
    socket.off(channel);
  },

  // Force reconnection
  forceReconnect: () => {
    console.log('🔄 Forcing socket reconnection...');
    socket.disconnect();
    setTimeout(() => {
      socket.connect();
    }, 1000);
  },

  // Get all active listeners (for debugging)
  getListeners: () => {
    const listeners = {};
    const events = ['connect', 'disconnect', 'connect_error', 'reconnect', 'reconnect_error', 'message', 'assignContact', 'pushNotification'];
    
    events.forEach(event => {
      listeners[event] = socket.listeners(event).length;
    });
    
    // Also check for user-specific channels
    const eventNames = socket.eventNames();
    eventNames.forEach(eventName => {
      if (typeof eventName === 'string' && (eventName.startsWith('message') || eventName.startsWith('assignContact') || eventName.startsWith('pushNotification'))) {
        listeners[eventName] = socket.listeners(eventName).length;
      }
    });
    
    return listeners;
  },

  // Test specific channel message
  testChannelMessage: (userId, isAdmin = false) => {
    const pushID = isAdmin ? "" : userId;
    const messageChannel = "message" + pushID;
    const testData = {
      entry: [{
        changes: [{
          value: {
            contacts: [{ wa_id: "1234567890", profile: { name: "Test Contact" } }],
            messages: [{ 
              id: "test_" + Date.now(),
              from: "1234567890",
              text: { body: "Test message from socketDebug" },
              timestamp: Math.floor(Date.now() / 1000).toString(),
              type: "text"
            }]
          }
        }]
      }]
    };
    
    console.log(`📤 Sending test message to channel: ${messageChannel}`);
    return socketDebug.testMessage(messageChannel, testData);
  },

  // Test authentication
  testAuthentication: (userId, ejecutivoId = null, isAdmin = false) => {
    if (!socket.connected) {
      console.error('❌ Cannot test authentication: Socket not connected');
      return false;
    }
    
    const authData = { userId };
    if (ejecutivoId) {
      authData.ejecutivoId = ejecutivoId;
    }
    if (isAdmin) {
      authData.isAdmin = true;
    }
    
    console.log('🔐 Testing authentication with:', authData);
    socket.emit('authenticate', authData);
    return true;
  },

  // Test admin authentication specifically
  testAdminAuthentication: (userId) => {
    return socketDebug.testAuthentication(userId, null, true);
  },

  // Test ejecutivo authentication specifically  
  testEjecutivoAuthentication: (userId, ejecutivoId) => {
    return socketDebug.testAuthentication(userId, ejecutivoId, false);
  }
};

// Make it available globally for debugging in browser console
if (typeof window !== 'undefined') {
  window.socketDebug = socketDebug;
}
